/**
 * 方案编写 —— 第 0 步：方案信息（方案名称 + 方案类型多选/自定义 + 方案模板）
 *
 * 与投标文件的「标书解析」不同：方案不是解析招标文件，而是录入
 * 方案名称并选择方案类型（立项报告/需求规格/概要设计/详细设计/技术方案/实施方案/用户手册/测试报告/验收报告，可自定义添加），
 * 多选则按每个类型生成一份文档；模板进知识库作为体例参考。
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  CloudArrowUpIcon,
  DocumentTextIcon,
  BookOpenIcon,
  CheckCircleIcon,
  TrashIcon,
  SparklesIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { projectApi, knowledgeApi, getErrorMessage } from '../services/api';
import type { ProjectInfo, KnowledgeBaseFile } from '../types';
import { SOLUTION_TYPES } from '../types';

interface SolutionSetupProps {
  project: ProjectInfo;
  onSaved: (project: ProjectInfo) => void;
  readOnly?: boolean;
}

const SolutionSetup: React.FC<SolutionSetupProps> = ({ project, onSaved, readOnly = false }) => {
  const [name, setName] = useState(project.name || '');
  const [solutionTypes, setSolutionTypes] = useState<string[]>(project.solution_types || []);
  const [solutionRequirements, setSolutionRequirements] = useState(project.solution_requirements || '');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [addingType, setAddingType] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const [templateFiles, setTemplateFiles] = useState<KnowledgeBaseFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const templateInputRef = useRef<HTMLInputElement>(null);

  // 方案模板存放的知识库：取项目关联的第一个知识库
  const templateKbId = project.knowledge_base_ids?.[0] ?? null;

  const loadTemplateFiles = useCallback(async (kbId: number | null) => {
    if (!kbId) {
      setTemplateFiles([]);
      return;
    }
    setLoadingFiles(true);
    try {
      const res = await knowledgeApi.listFiles(kbId);
      setTemplateFiles(res.data.files || []);
    } catch {
      setTemplateFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  }, []);

  useEffect(() => {
    void loadTemplateFiles(templateKbId);
  }, [templateKbId, loadTemplateFiles]);

  const toggleSolutionType = (type: string) => {
    setSolutionTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  // 自定义方案类型（不在内置 SOLUTION_TYPES 中、但已被选中的）
  const builtinTypes = SOLUTION_TYPES as readonly string[];
  const customTypes = solutionTypes.filter((t) => !builtinTypes.includes(t));

  const addCustomType = () => {
    const t = newTypeName.trim();
    if (!t) {
      setAddingType(false);
      return;
    }
    setSolutionTypes((prev) => (prev.includes(t) ? prev : [...prev, t]));
    setNewTypeName('');
    setAddingType(false);
  };

  const cancelAddType = () => {
    setAddingType(false);
    setNewTypeName('');
  };

  const removeCustomType = (type: string) => {
    setSolutionTypes((prev) => prev.filter((t) => t !== type));
  };

  // AI 生成方案需求/功能（基于方案名称与类型，作为上下文）
  const handleAiGenerateRequirements = async () => {
    if (!name.trim()) {
      setMessage({ type: 'error', text: '请先填写方案名称' });
      return;
    }
    if (!solutionTypes.length) {
      setMessage({ type: 'error', text: '请先选择方案类型' });
      return;
    }
    setAiGenerating(true);
    setMessage(null);
    setSolutionRequirements('');
    try {
      const full = await projectApi.generateSolutionRequirementsStream(
        project.id,
        name.trim(),
        solutionTypes,
        solutionRequirements || undefined,
        (fullText) => setSolutionRequirements(fullText),
      );
      setSolutionRequirements(full.trim());
      setMessage({ type: 'success', text: '已生成方案需求/功能，可继续编辑后保存' });
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, 'AI 生成失败') });
    } finally {
      setAiGenerating(false);
    }
  };

  const save = async () => {
    if (!name.trim()) {
      setMessage({ type: 'error', text: '请填写方案名称' });
      return;
    }
    if (!solutionTypes.length) {
      setMessage({ type: 'error', text: '请至少选择一个方案类型' });
      return;
    }
    try {
      setSaving(true);
      setMessage(null);
      const res = await projectApi.update(project.id, {
        name: name.trim(),
        solution_types: solutionTypes,
        solution_requirements: solutionRequirements,
      });
      onSaved(res.data);
      setMessage({ type: 'success', text: '方案信息已保存' });
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '保存失败') });
    } finally {
      setSaving(false);
    }
  };

  // 上传方案模板到知识库：作为体例参考参与目录/正文生成（支持多文件）
  const handleTemplateUpload = async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (!list.length) return;
    try {
      setUploadingTemplate(true);
      setMessage(null);
      let kbId = templateKbId;
      if (!kbId) {
        const kb = await knowledgeApi.create({ name: `【方案模板】${project.name}` });
        kbId = kb.data.id;
        const updated = await projectApi.update(project.id, {
          knowledge_base_ids: [kbId, ...(project.knowledge_base_ids || [])],
        });
        onSaved(updated.data);
      }
      await knowledgeApi.uploadFiles(kbId, list);
      await loadTemplateFiles(kbId);
      setMessage({ type: 'success', text: `已上传 ${list.length} 个方案模板到知识库` });
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '方案模板上传失败') });
    } finally {
      setUploadingTemplate(false);
    }
  };

  const handleTemplateDelete = async (fileId: number) => {
    if (!templateKbId) return;
    if (!window.confirm('确定删除该方案模板吗？')) return;
    try {
      await knowledgeApi.deleteFile(templateKbId, fileId);
      await loadTemplateFiles(templateKbId);
      setMessage({ type: 'success', text: '模板已删除' });
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '删除失败') });
    }
  };

  const fieldCls =
    'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400';

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* 标题 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-1">📐 方案信息</h2>
        <p className="text-sm text-gray-500">
          填写方案名称并选择方案类型（可多选，每个类型生成一份文档），可上传方案模板作为体例参考。
        </p>
      </div>

      {/* 方案基本信息 */}
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <h3 className="text-sm font-semibold text-gray-800">方案基本信息</h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">方案名称</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={readOnly}
            className={fieldCls}
            placeholder="请输入方案名称，如：智慧水务管网监测系统技术方案"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">方案类型（可多选）</label>
          <div className="flex flex-wrap gap-2">
            {SOLUTION_TYPES.map((type) => {
              const checked = solutionTypes.includes(type);
              return (
                <button
                  key={type}
                  type="button"
                  disabled={readOnly}
                  onClick={() => toggleSolutionType(type)}
                  className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                    checked
                      ? 'bg-blue-50 text-blue-700 border-blue-300'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  } disabled:opacity-50`}
                >
                  {type}
                </button>
              );
            })}
            {customTypes.map((type) => (
              <span
                key={type}
                className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm border bg-blue-50 text-blue-700 border-blue-300"
              >
                {type}
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => removeCustomType(type)}
                    className="ml-1.5 text-blue-400 hover:text-blue-700"
                    title="删除该自定义类型"
                  >
                    <XMarkIcon className="w-3.5 h-3.5" />
                  </button>
                )}
              </span>
            ))}
            {!readOnly &&
              (addingType ? (
                <span className="inline-flex items-center gap-1">
                  <input
                    autoFocus
                    type="text"
                    value={newTypeName}
                    onChange={(e) => setNewTypeName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                        e.preventDefault();
                        addCustomType();
                      } else if (e.key === 'Escape') {
                        cancelAddType();
                      }
                    }}
                    onBlur={() => {
                      // 失焦时提交：点击「添加」「保存」或页面其它位置都会生效
                      if (newTypeName.trim()) addCustomType();
                      else cancelAddType();
                    }}
                    placeholder="如：产品报告"
                    className="w-32 px-3 py-1.5 rounded-lg text-sm border border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={addCustomType}
                    className="px-2 py-1.5 rounded-lg text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100"
                  >
                    添加
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setAddingType(true)}
                  className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-sm text-blue-600 border border-dashed border-blue-300 hover:bg-blue-50"
                  title="添加自定义方案类型"
                >
                  <PlusIcon className="w-4 h-4" />
                </button>
              ))}
          </div>
          {solutionTypes.length > 0 && (
            <p className="mt-2 text-xs text-gray-500">
              已选 {solutionTypes.length} 个类型：{solutionTypes.join('、')}
            </p>
          )}
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-700">方案需求/功能</label>
            <button
              type="button"
              onClick={() => void handleAiGenerateRequirements()}
              disabled={readOnly || aiGenerating}
              className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md text-purple-700 bg-purple-50 hover:bg-purple-100 disabled:opacity-50"
              title="AI 根据方案名称与类型生成需求/功能"
            >
              <SparklesIcon className="w-3.5 h-3.5 mr-1" />
              {aiGenerating ? '生成中...' : 'AI 生成'}
            </button>
          </div>
          <textarea
            value={solutionRequirements}
            onChange={(e) => setSolutionRequirements(e.target.value)}
            rows={6}
            disabled={readOnly}
            className={fieldCls}
            placeholder="描述方案需要满足的需求与功能，例如：支持多用户角色权限、数据大屏实时刷新、设备告警联动等（将作为上下文传给 AI）"
          />
        </div>
      </div>

      {/* 方案模板 */}
      <div className="bg-white rounded-lg shadow p-6 space-y-5">
        <h3 className="text-sm font-semibold text-gray-800">方案模板</h3>

        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <BookOpenIcon className="w-5 h-5 text-purple-600" />
              <div>
                <div className="text-sm font-medium text-gray-800">方案模板（进知识库）</div>
                <div className="text-xs text-gray-400">
                  {templateFiles.length ? (
                    <span className="flex items-center gap-1 text-green-600">
                      <CheckCircleIcon className="w-3.5 h-3.5" /> 已上传 {templateFiles.length} 个模板
                    </span>
                  ) : (
                    '上传企业/行业方案模板，作为体例参考参与目录与正文生成（可上传多个）'
                  )}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => templateInputRef.current?.click()}
              disabled={readOnly || uploadingTemplate}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg text-purple-700 bg-purple-50 hover:bg-purple-100 disabled:opacity-50"
            >
              <CloudArrowUpIcon className="w-4 h-4 mr-1" />
              {uploadingTemplate ? '上传中...' : '上传模板'}
            </button>
            <input
              ref={templateInputRef}
              type="file"
              accept=".pdf,.docx,.doc,.xlsx,.xls"
              multiple
              className="hidden"
              disabled={readOnly}
              onChange={(e) => {
                const files = e.target.files;
                if (files && files.length) void handleTemplateUpload(files);
                e.target.value = '';
              }}
            />
          </div>

          {loadingFiles ? (
            <div className="mt-3 text-xs text-gray-400">加载模板列表...</div>
          ) : templateFiles.length > 0 ? (
            <ul className="mt-3 space-y-1.5">
              {templateFiles.map((f) => (
                <li
                  key={f.id}
                  className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-gray-50 text-sm"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <DocumentTextIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="truncate text-gray-700">{f.original_name}</span>
                  </span>
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => void handleTemplateDelete(f.id)}
                      className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50"
                      title="删除模板"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>

      {/* 保存 */}
      {!readOnly && (
        <div className="flex justify-end">
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center justify-center px-6 py-2.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {saving ? '保存中...' : '保存方案信息'}
          </button>
        </div>
      )}

      {/* 消息提示 */}
      {message && (
        <div
          className={`p-4 rounded-md ${
            message.type === 'success'
              ? 'bg-green-100 text-green-700 border border-green-200'
              : 'bg-red-100 text-red-700 border border-red-200'
          }`}
        >
          {message.text}
        </div>
      )}
    </div>
  );
};

export default SolutionSetup;
